const instances = new WeakMap();
const cleanQuery = value => String(value || '').trim().replace(/\s+/g, ' ');
const normalise = value => value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim().toLowerCase();
export function suggestionURL(value, query) {
  const url = new URL(value, location.origin);
  if (url.origin !== location.origin || !/\/search\/suggest\/?$/.test(url.pathname)) throw new Error('Invalid search source.');
  url.search = ''; url.hash = '';
  url.searchParams.set('q', cleanQuery(query));
  url.searchParams.set('resources[type]', 'product');
  url.searchParams.set('resources[limit]', '6');
  url.searchParams.set('resources[options][unavailable_products]', 'last');
  url.searchParams.set('section_id', 'bb-predictive-search');
  return url;
}
export function searchResultsURL(form, query) {
  const text = cleanQuery(query);
  const artist = [...form.querySelectorAll('datalist option')].find(option => normalise(option.value) === normalise(text));
  const url = new URL(artist ? form.dataset.bbArtistUrl : form.action, location.origin);
  if (url.origin !== location.origin) throw new Error('Search must stay on this store.');
  url.search = ''; url.hash = '';
  if (artist) url.searchParams.set(form.dataset.bbArtistParam, artist.dataset.filterValue);
  else { url.searchParams.set('q', text); url.searchParams.set('type', 'product'); url.searchParams.set('options[prefix]', 'last'); }
  return url;
}
export function unpackSuggestions(value) {
  if (!Array.isArray(value) || value.length > 6) throw new Error('Invalid search results.');
  const seen = new Set();
  return value.flatMap(row => {
    if (!Array.isArray(row) || row.length !== 6 || !row.slice(0, 5).every(item => typeof item === 'string') || !/^\d+$/.test(row[0]) || typeof row[5] !== 'boolean') throw new Error('Invalid search result.');
    const url = new URL(row[2], location.origin);
    if (url.origin !== location.origin || !/\/products\/[^/]+\/?$/.test(url.pathname)) throw new Error('Invalid product link.');
    let image = '';
    if (row[3]) { const photo = new URL(row[3], location.origin); if (photo.protocol === 'https:') image = photo.href; }
    if (seen.has(row[0])) return []; seen.add(row[0]);
    return [{ id: row[0], title: row[1], url: url.href, image, price: row[4], available: row[5] }];
  });
}
export class PredictiveSearch {
  constructor(form) {
    this.form = form; this.input = form.querySelector('input[name="q"]');
    this.panel = form.querySelector('[data-bb-search-panel]');
    this.list = form.querySelector('[data-bb-search-results]');
    this.status = form.querySelector('[data-bb-search-status]');
    this.clear = form.querySelector('[data-bb-search-clear]');
    this.closeButton = form.querySelector('[data-bb-search-close]');
    if (!this.input || !this.panel || !this.list || !this.status) return;
    this.events = new AbortController(); this.cache = new Map(); this.version = 0; this.active = -1; this.links = []; this.composing = false;
    this.oldList = this.input.getAttribute('list'); this.input.removeAttribute('list');
    this.input.setAttribute('role', 'combobox'); this.input.setAttribute('aria-autocomplete', 'list');
    this.input.setAttribute('aria-controls', this.list.id); this.input.setAttribute('aria-expanded', 'false');
    this.input.setAttribute('aria-describedby', this.status.id);
    form.dataset.predictiveReady = 'true';
    const options = { signal: this.events.signal };
    this.input.addEventListener('input', () => { if (!this.composing) this.schedule(); }, options);
    this.input.addEventListener('compositionstart', () => { this.composing = true; this.cancel(); this.close(); }, options);
    this.input.addEventListener('compositionend', () => { this.composing = false; this.schedule(); }, options);
    this.input.addEventListener('focus', () => { if (cleanQuery(this.input.value).length >= 2) this.schedule(); }, options);
    this.input.addEventListener('keydown', event => this.key(event), options);
    this.clear?.addEventListener('click', () => { this.input.value = ''; this.cancel(); this.close(); this.status.textContent = ''; this.clear.hidden = true; this.input.focus(); }, options);
    this.closeButton?.addEventListener('click', () => { this.input.focus(); this.cancel(); this.close(); }, options);
    form.addEventListener('submit', () => {
      this.cancel(); this.close(); // Enter without a chosen suggestion keeps the existing search/artist behaviour.
    }, options);
    form.addEventListener('focusout', event => { if (!form.contains(event.relatedTarget)) { this.cancel(); this.close(); } }, options);
    document.addEventListener('pointerdown', event => { if (!form.contains(event.target)) { this.cancel(); this.close(); } }, options);
    this.list.addEventListener('pointermove', event => {
      const link = event.target.closest('[data-bb-search-choice]');
      if (link) this.highlight(this.links.indexOf(link), false);
    }, options);
    this.list.addEventListener('click', () => { this.cancel(); this.close(); }, options);
    window.addEventListener('resize', () => this.fit(), options);
    window.visualViewport?.addEventListener('resize', () => this.fit(), options);
    window.visualViewport?.addEventListener('scroll', () => this.fit(), options);
    if (this.clear) this.clear.hidden = !this.input.value;
  }
  cancel() { clearTimeout(this.timer); this.request?.abort(); this.version++; this.form.removeAttribute('aria-busy'); }
  close() {
    this.panel.hidden = true; this.input.setAttribute('aria-expanded', 'false'); this.input.removeAttribute('aria-activedescendant');
    this.active = -1;
    this.list.querySelectorAll('[role="option"]').forEach(option => option.setAttribute('aria-selected', 'false'));
  }
  fit() {
    const viewport = window.visualViewport;
    const bottom = viewport ? viewport.height + viewport.offsetTop : window.innerHeight;
    const space = Math.max(100, bottom - this.form.getBoundingClientRect().bottom - 12);
    this.panel.style.setProperty('--bb-search-space', space + 'px');
  }
  schedule() {
    this.cancel(); this.close();
    if (this.clear) this.clear.hidden = !this.input.value;
    const query = cleanQuery(this.input.value);
    if (query.length < 2) { this.status.textContent = ''; return; }
    const version = this.version;
    this.timer = setTimeout(() => this.load(query, version), 200);
  }
  async load(query, version) {
    const request = new AbortController(); this.request = request;
    this.form.setAttribute('aria-busy', 'true'); this.status.textContent = 'Searching cards…';
    try {
      const url = suggestionURL(this.form.dataset.predictiveUrl, query), key = url.href;
      const previous = this.cache.get(key);
      let rows = previous && Date.now() - previous.time < 30000 ? previous.rows : null;
      if (!rows) {
        const response = await fetch(url.href, { credentials: 'same-origin', signal: request.signal });
        if (!response.ok) throw new Error('Search suggestions unavailable.');
        const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
        const feed = parsed.querySelector('[data-bb-predictive-feed]');
        if (!feed || cleanQuery(feed.dataset.query) !== query) throw new Error('Search results changed.');
        rows = unpackSuggestions(JSON.parse(feed.querySelector('[data-bb-predictive-rows]')?.textContent || 'null'));
        if (this.cache.size >= 24) this.cache.delete(this.cache.keys().next().value);
        this.cache.set(key, { time: Date.now(), rows });
      }
      if (request.signal.aborted || version !== this.version || cleanQuery(this.input.value) !== query || !this.form.contains(document.activeElement)) return;
      if (!rows.length) { this.close(); this.status.textContent = 'No suggestions. Press Enter to search all cards.'; return; }
      this.render(rows, query);
    } catch {
      if (request.signal.aborted || version !== this.version) return;
      this.close(); this.status.textContent = 'Suggestions unavailable. Press Enter to search all cards.';
    } finally {
      if (this.request === request) this.form.removeAttribute('aria-busy');
    }
  }
  render(rows, query) {
    const options = rows.map((row, index) => {
      const li = document.createElement('li'); li.id = this.list.id + '-' + index; li.setAttribute('role', 'option'); li.setAttribute('aria-selected', 'false');
      const a = document.createElement('a'); a.href = row.url; a.tabIndex = -1; a.dataset.bbSearchChoice = '';
      const image = document.createElement('span'); image.className = 'bb-search-photo';
      if (row.image) { const img = document.createElement('img'); img.src = row.image; img.alt = ''; img.width = 48; img.height = 64; img.loading = 'lazy'; img.decoding = 'async'; image.append(img); }
      const copy = document.createElement('span'); copy.className = 'bb-search-copy';
      const title = document.createElement('span'); title.className = 'bb-search-title'; title.textContent = row.title; copy.append(title);
      if (!row.available) { const badge = document.createElement('span'); badge.className = 'bb-search-sold'; badge.textContent = 'Sold out'; copy.append(badge); }
      const price = document.createElement('span'); price.className = 'bb-search-price'; price.textContent = row.price;
      a.append(image, copy, price); li.append(a); return li;
    });
    const more = document.createElement('li'); more.id = this.list.id + '-all'; more.setAttribute('role', 'option'); more.setAttribute('aria-selected', 'false');
    const a = document.createElement('a'); a.href = searchResultsURL(this.form, query).href; a.tabIndex = -1; a.dataset.bbSearchChoice = ''; a.className = 'bb-search-all'; a.textContent = 'View all results for “' + query + '” →'; more.append(a);
    this.list.replaceChildren(...options, more); this.links = [...this.list.querySelectorAll('[data-bb-search-choice]')];
    this.active = -1; this.panel.hidden = false; this.panel.scrollTop = 0; this.fit(); this.input.setAttribute('aria-expanded', 'true');
    this.status.textContent = rows.length + (rows.length === 1 ? ' card suggested.' : ' cards suggested.') + ' Use up and down arrows to choose, or Enter to search all results.';
  }
  highlight(index, scroll = true) {
    if (!this.links.length || index < 0) return;
    this.active = index;
    this.links.forEach((link, position) => link.parentElement.setAttribute('aria-selected', String(position === index)));
    this.input.setAttribute('aria-activedescendant', this.links[index].parentElement.id);
    if (scroll) this.links[index].scrollIntoView({ block: 'nearest' });
  }
  key(event) {
    if (event.isComposing || this.composing) return;
    if (event.key === 'Escape') { event.preventDefault(); this.cancel(); this.close(); return; }
    if (event.key === 'Tab') { this.cancel(); this.close(); return; }
    if (this.panel.hidden || !this.links.length) return;
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const index = this.active < 0 ? event.key === 'ArrowDown' ? 0 : this.links.length - 1 : (this.active + (event.key === 'ArrowDown' ? 1 : -1) + this.links.length) % this.links.length;
      this.highlight(index);
    } else if (event.key === 'Enter' && this.active >= 0) {
      event.preventDefault(); event.stopPropagation(); this.links[this.active].click();
    }
  }
  destroy() {
    this.cancel(); this.close(); this.events.abort(); delete this.form.dataset.predictiveReady;
    ['role', 'aria-autocomplete', 'aria-controls', 'aria-expanded', 'aria-describedby', 'aria-activedescendant'].forEach(name => this.input.removeAttribute(name));
    if (this.oldList) this.input.setAttribute('list', this.oldList);
  }
}
function initialise(root = document) {
  root.querySelectorAll('[data-bb-predictive]').forEach(form => { if (!instances.has(form)) instances.set(form, new PredictiveSearch(form)); });
}
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialise());
else initialise();
document.addEventListener('shopify:section:load', event => initialise(event.target));
document.addEventListener('shopify:section:unload', event => event.target.querySelectorAll('[data-bb-predictive]').forEach(form => { instances.get(form)?.destroy(); instances.delete(form); }));
